import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../../../components/AuthContext';
import { useToast } from '../../../components/ToastContext';
import { dayAndClock } from '../../../lib/when';
import { newestReports, uploadReport, today, REPORT_LABEL } from './morningReports';
import Pill from '../../../components/Pill';

/*
  TODAY'S REPORTS, on the Job Queue. Ordering stage 3, 11 October 2026.

  **The two files that come in every day**, his answer of 11 October: *"the only
  files that would be uploaded every day would be the excess calc and stock
  status reports."* One box each, saying when it was last loaded and whether
  that was today. A report from an earlier day is shown in the warning colour,
  because a run on yesterday's stock would order the wrong amounts.
*/
const ORDER = ['stock_status', 'excess_calc'];
const SAYS = {
  stock_status: 'Par, on hand and on order at every warehouse',
  excess_calc: "Austin's stock, from Account Edge",
};

export default function TodaysReports({ onLoaded }) {
  const { user } = useAuth();
  const toast = useToast();
  const [reports, setReports] = useState(null);
  const [failed, setFailed] = useState(null);
  const [busy, setBusy] = useState(null);       // { kind, done, of }
  const [said, setSaid] = useState({});         // kind -> { ok, text }
  const pick = { stock_status: useRef(null), excess_calc: useRef(null) };

  const read = useCallback(async () => {
    const r = await newestReports();
    if (r.error) { setFailed(r.error.message); return; }
    setFailed(null);
    setReports(r.reports);
  }, []);
  useEffect(() => { read(); }, [read]);

  const upload = async (kind, file) => {
    if (!file) return;
    setBusy({ kind, done: 0, of: 0 });
    setSaid(s => ({ ...s, [kind]: null }));
    try {
      const r = await uploadReport(file, kind, user && user.id, (done, of) => setBusy({ kind, done, of }));
      if (!r.ok) { setSaid(s => ({ ...s, [kind]: { ok: false, text: r.problem } })); return; }
      const bits = [r.kept.toLocaleString() + ' rows kept'];
      if (r.empty) bits.push(r.empty.toLocaleString() + ' with nothing in them left out');
      if (r.unreadable) bits.push(r.unreadable.toLocaleString() + ' numbers that could not be read, taken as 0');
      setSaid(s => ({ ...s, [kind]: { ok: true, text: file.name + ': ' + bits.join(', ') + '.' } }));
      toast.success(REPORT_LABEL[kind] + ' loaded');
      await read();
      onLoaded && onLoaded();
    } catch (e) {
      setSaid(s => ({ ...s, [kind]: { ok: false, text: 'That file could not be read: ' + (e && e.message ? e.message : String(e)) } }));
    } finally {
      setBusy(null);
      if (pick[kind].current) pick[kind].current.value = '';
    }
  };

  const now = today();
  /* Drawn on the house panel pieces from 12 October 2026, when he found the
     queue cramped and unlike the rest of OnHand: a section title, then one row
     per report with a badge for whether it is today's. */
  return (
    <div className="card" style={{ padding: '16px 20px', marginBottom: 16 }}>
      <div className="ua-section-head" style={{ marginBottom: 10 }}>
        <div className="ua-section-title" style={{ fontSize: 15 }}>Today's reports</div>
        <div className="ua-section-note" style={{ fontSize: 13 }}>Loaded once each morning. Every run that day uses them.</div>
      </div>
      {failed && <p style={{ color: 'var(--critical-ink)', margin: '0 0 8px', fontSize: 14 }}>OnHand could not read the reports just now: {failed}</p>}
      {ORDER.map((kind, i) => {
        const r = reports && reports[kind];
        const isToday = r && r.report_day === now;
        const mine = busy && busy.kind === kind;
        return (
          <div key={kind} style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap', padding: '12px 0', borderTop: i ? '1px solid var(--border-soft)' : 'none' }}>
            <div style={{ flex: '1 1 260px', minWidth: 0 }}>
              <div style={{ fontSize: 15, fontWeight: 700 }}>{REPORT_LABEL[kind]}</div>
              <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 2 }}>
                {SAYS[kind]}
                {r ? '. Last loaded ' + dayAndClock(r.loaded_at) + ', ' + Number(r.rows_kept).toLocaleString() + ' rows.' : '.'}
              </div>
              {said[kind] && (
                <div style={{ fontSize: 13, marginTop: 4, color: said[kind].ok ? 'var(--text-secondary)' : 'var(--critical-ink)' }}>{said[kind].text}</div>
              )}
            </div>
            <div>
              {reports === null ? null : !r ? <Pill tone="red">Never loaded</Pill> : isToday ? <Pill tone="app">Loaded today</Pill> : <Pill tone="gold">Not today's</Pill>}
            </div>
            <input ref={pick[kind]} type="file" accept=".csv,.txt,.tsv,.xlsx,.xls,text/csv" style={{ display: 'none' }}
              aria-label={'Upload the ' + REPORT_LABEL[kind]}
              onChange={e => upload(kind, e.target.files && e.target.files[0])} />
            <button type="button" className="btn btn-quiet" disabled={!!busy}
              onClick={() => pick[kind].current && pick[kind].current.click()}>
              {mine ? (busy.of ? 'Saving ' + busy.done.toLocaleString() + ' of ' + busy.of.toLocaleString() : 'Reading...') : isToday ? 'Load again' : 'Upload'}
            </button>
          </div>
        );
      })}
    </div>
  );
}
